import React, {useState, useEffect, useRef, useContext, Fragment} from "react";
import {ATTEndpoints} from "../../../../../services/att/endpoints";
import CustomDataGrid, {DataGridRef} from "../../../../../components/data-grid/CustomDataGrid";
import {
  GridColDef,
  GridFilterModel,
  GridFilterOperator,
  GridFilterItem,
  GridFilterInputValueProps, GridRenderCellParams,
} from "@mui/x-data-grid";
import {
  Box,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  TextField,
  Dialog as MuiDialog,
  DialogTitle,
  DialogContent,
  Typography,
  Alert,
  Button, Stack, FormControl, MenuItem,
} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers";
import {useTranslation} from "react-i18next";
import WebURL from "@/urls";
import CloseIcon from "@mui/icons-material/Close";
import CustomBreadcrumbs from "@/components/breadcrumbs/CustomBreadcrumbs";
import ExportAction from "@/components/actions/Export";
import SnackbarUtil from "@/utils/SnackbarUtil";
import Util from "@/utils/Util";
import moment from "moment";
import TimecardService from "@/services/att/services/TimecardService";
import TransactionService from "@/services/att/services/TransactionService";
import {DataGridContext, DataGridDispatchContext} from "@/components/data-grid/DataGridContext";
import TimeSelectDrawer from "@/pages/att/Timecard/TimeSelectDrawer";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import CompanyService from "@/services/auth/CompanyService";

const SUBMIT_FILTER_STROKE_TIME = 500;

interface InputNumberIntervalExtraProps {
  dateFormat: string;
}

type InputNumberIntervalProps = GridFilterInputValueProps & InputNumberIntervalExtraProps;

function InputNumberInterval(props: InputNumberIntervalProps) {
  const {t} = useTranslation();
  const {item, applyValue, dateFormat} = props;

  const filterTimeout = useRef<any>();
  const [filterValueState, setFilterValueState] = useState<[Date, Date]>([item.value[0], item.value[1]]);

  useEffect(() => {
    return () => {
      clearTimeout(filterTimeout.current);
    };
  }, []);

  const updateFilterValue = (lowerBound: Date, upperBounding: Date) => {
    clearTimeout(filterTimeout.current);
    setFilterValueState([lowerBound, upperBounding]);

    filterTimeout.current = setTimeout(() => {
      applyValue({...item, value: [lowerBound, upperBounding]});
    }, SUBMIT_FILTER_STROKE_TIME)
  };

  const handleUpperFilterChange = (event: any) => {
    const newUpperBound = event;
    updateFilterValue(filterValueState[0], newUpperBound);
  };
  const handleLowerFilterChange = (event: any) => {
    const newLowerBound = event;
    updateFilterValue(newLowerBound, filterValueState[1]);
  };

  return (
    <Box
      sx={{
        display: 'inline-flex',
        flexDirection: 'row',
        alignItems: 'end',
        width: "240px"
      }}
    >
      <DatePicker
        label={t("common.Start Date")}
        value={filterValueState[0] || new Date(new Date().getFullYear(), new Date().getMonth(), 1)}
        maxDate={filterValueState[1]}
        inputFormat={dateFormat}
        onChange={handleLowerFilterChange}
        renderInput={(params) =>
          <TextField
            variant="standard"
            {...params}
          />}
      />
      <DatePicker
        label={t("common.End Date")}
        value={filterValueState[1]}
        minDate={filterValueState[0]}
        inputFormat={dateFormat}
        onChange={handleUpperFilterChange}
        renderInput={(params) =>
          <TextField
            variant="standard"
            {...params}
          />}
      />
    </Box>
  )
}

const punchRecordStyle = {
  '& .MuiPaper-root': {width: '60vw', top: '-5vh'},
  '.MuiDialog-paper': {maxWidth: '100vw'},
};

const TransactionGrid: React.FC = () => {
  const {t} = useTranslation();
  const today = new Date()
  const firstDay = new Date()
  firstDay.setDate(1)
  const [mended, setMended] = useState<boolean>(false);
  const dataGridRef = useRef<DataGridRef>();
  const [info, setInfo] = useState<boolean>(false);
  const [filterParams, setFilterParams] = useState<Record<any, any>>({
    date_range: [
      firstDay.toISOString().slice(0, 10),
      today.toISOString().slice(0, 10)
    ]
  });
  const [exportFormat, setExportFormat] = useState<string>("");
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [exportLoading, setExportLoading] = useState(false);
  const [filterDate, setFilterDate] = useState<GridFilterModel>({
    items: [
      {
        id: 1,
        columnField: 'att_date',
        value: [firstDay.toISOString().slice(0, 10), today.toISOString().slice(0, 10)],
        operatorValue: "between",
      }
    ]
  })
  const [dateFormat, setDateFormat] = useState<string>("MM/dd/yyyy")
  const VERIFY_TYPE = {
    2: t('common.Pin'),
    4: t('common.Card'),
    1: t('common.Fingerprint'),
    0: t('common.Face'),
    3: t('common.Password'),
    10: t('common.Punch'),
  }
  const [verifyType, setVerifyType] = React.useState<string>("");
  const reloadTable = () => {
    dataGridRef.current?.refresh();
  }

  function debounce(fn: any, delay: any) {
    let timer: any;
    return function (...args: any) {
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(() => {
        fn(...args);
      }, delay);
    };
  }

  const debouncedSave = debounce((newValue: any) => {
    reloadTable()
  }, 1000);

  useEffect(() => {
    debouncedSave(verifyType);
  }, [verifyType]);
  useEffect(() =>{
    CompanyService.dateFormat({method: 'get'}).then((res) => {
      const date_format = res.data?.date_format?.toString() || "MM/dd/yyyy";
      setDateFormat(date_format);
    });
  }, [])

  const InfoDialog = () => {
    return (
      <MuiDialog
        open={info}
        sx={punchRecordStyle}
      >
        <DialogTitle>
          {t("common.Usage Tips")}
          <IconButton
            aria-label="close"
            onClick={() => setInfo(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon/>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{lineHeight: '25px', color: 'gray'}}>
          <Box sx={{p: 1}}> 1. {t("dms.punchRecordTip")}</Box>
        </DialogContent>
      </MuiDialog>
    )
  }

  const WrappedInputNumberInterval = (props: GridFilterInputValueProps) => {
    return <InputNumberInterval {...props as InputNumberIntervalProps} dateFormat={dateFormat}/>;
  };
  const punchDateOperators: GridFilterOperator[] = [
    {
      label: t("common.Between"),
      value: "between",
      getApplyFilterFn: (filterItem: GridFilterItem, column) => {
        if (!Array.isArray(filterItem.value) || filterItem.value.length !== 2) {
          return null;
        }
        return ({value}) => {
          return true;
        };
      },
      InputComponent: WrappedInputNumberInterval,
    },
  ];
  const SourceField = (params: GridRenderCellParams) => {
    let punch_from = "";
    if (params.value == 'manual') {
      punch_from = t("common.manual");
    } else {
      punch_from = params.value
    }
    return <Grid>{punch_from}</Grid>;
  };
  useEffect(() => {
    if (exportFormat) {
      setExportLoading(true);
      const export_meta = {
        fields: {
          employee_code: { alias: t("common.Person ID") },
          employee_name: { alias: t("common.Person Name") },
          att_date: { alias: t("common.Punch Date") },
          attendance_status: { alias: t("common.Attendance record") },
          verify_type: { alias: t("common.Verify Type") },
          timezone: { alias: t("att.schedule.TimeZone") },
          punch_from: { alias: t("common.punch from") },
        },
        title: t("common.View Attendance Punch"),
        file_type: exportFormat,
        share: [],
      };

      try {
        const data = export_meta;
        const params = `&keyword=${searchKeyword}&date_range=${moment(filterParams?.date_range[0]).format(
          "yyyy-MM-DD"
        )}&date_range=${moment(filterParams?.date_range[1]).format(
          "yyyy-MM-DD"
        )}&verify_type=${verifyType}`;

        // 修复大数据下载 + 异步不阻塞
        TransactionService.download_data(params, data)
          .then((response) => {
            if (!response || !response.data) throw new Error("Empty file");

            const contentType = response.headers?.["content-type"] || "application/octet-stream";
            const blob = new Blob([response.data], { type: contentType });

            // 异步执行，防止大文件卡死
            setTimeout(() => {
              const url = URL.createObjectURL(blob);
              const aLink = document.createElement("a");
              aLink.style.display = "none";
              aLink.href = url;

              // 自动读取文件名
              const disp = response.headers?.["content-disposition"];
              if (disp) {
                const fn = disp.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (fn && fn[1]) aLink.download = fn[1].replace(/['"]/g, "");
              }

              document.body.appendChild(aLink);
              aLink.click();

              setTimeout(() => {
                document.body.removeChild(aLink);
                URL.revokeObjectURL(url);
              }, 100);
            }, 0);

            SnackbarUtil.success(t("common.success"));
          })
          .catch((err) => {
            console.error("Download error", err);
            SnackbarUtil.error(t("common.Download") + t("common.failure"), {
              anchorOrigin: {
                vertical: "top",
                horizontal: "center"
              }
            });
          })
          .finally(() => {
            setExportLoading(false); //  关闭 Loading
            setExportFormat("");
          });

      } catch (e) {
        console.error(e);
        setExportLoading(false);
        setExportFormat("");
      }
    }
  }, [exportFormat, filterParams, searchKeyword, verifyType, t]);

  const columns: GridColDef[] = [
    {
      field: 'employee_name', headerName: t("common.Person Name"), minWidth: 200, flex: 0.2, filterable: false,
      renderCell: (params) => {
        return (
          <Grid style={{display: "flex", alignItems: "center"}}>
            <Box style={{padding: '0 8px'}}>{params.row.first_name} {params.row.last_name}</Box>
          </Grid>
        )
      }
    },
    {
      field: 'employee_code',
      headerName: t("common.Person ID"),
      type: "string",
      minWidth: 200,
      flex: 0.2,
      filterable: false
    },
    {
      field: 'att_date', headerName: t("common.Punch Date"), type: "date", minWidth: 150, flex: 0.2,
      filterOperators: punchDateOperators,
    },
    {
      field: 'attendance_status',
      headerName: t("common.Attendance record"),
      flex: 0.8,
      minWidth: 150,
      filterable: false,
      renderCell: (params) => {
        if (params.row.punch_from === 'manual') {
          return (
            <Grid color={mended ? "red" : "black"}>{params.row.attendance_status}</Grid>
          )
        } else {
          return (
            <Grid color={"black"}>{params.row.attendance_status}</Grid>
          )
        }
      }
    },
    {
      field: 'verify_type',
      headerName: t("common.Verify Type"),
      minWidth: 100,
      flex: 1,
      filterable: false,
      renderCell: (params) => {
        const baseStyles = {
          padding: '4px 12px',
          borderRadius: 4,
          textAlign: 'center',
          display: 'inline-block',
          minWidth: '100px'
        };

        switch (params.row.verify_type) {
          case 'Pin':
            return (
              <Grid sx={{
                ...baseStyles,
                color: "#2BA9DE",
                backgroundColor: "#E5F4FB"
              }}>
                {params.row.verify_type}
              </Grid>
            );
          case 'Card':
            return (
              <Grid sx={{
                ...baseStyles,
                color: "#00C8E0",
                backgroundColor: "#E6FAFC"
              }}>
                {params.row.verify_type}
              </Grid>
            );
          case 'Fingerprint':
            return (
              <Grid sx={{
                ...baseStyles,
                color: "#7AC143",
                backgroundColor: "#F1F8EC"
              }}>
                {params.row.verify_type}
              </Grid>
            );
          case 'Face':
            return (
              <Grid sx={{
                ...baseStyles,
                color: "#B02BD4",
                backgroundColor: "#F7E9FA"
              }}>
                {params.row.verify_type}
              </Grid>
            );
          case 'Punch':
            return (
              <Grid sx={{
                ...baseStyles,
                color: "#F4521F",
                backgroundColor: "#FEEEE9"
              }}>
                {params.row.verify_type}
              </Grid>
            );
          default:
            return (
              <Grid sx={{
                ...baseStyles,
                color: "black",
                backgroundColor: "transparent"
              }}>
                {params.row.verify_type || '-'}
              </Grid>
            );
        }
      }
    },
    {field: 'timezone', headerName: t("att.schedule.TimeZone"), minWidth: 150, flex: 1, filterable: false,},
    {field: 'punch_from', headerName: t("common.punch from"), flex: 1, renderCell: SourceField, filterable: false,},
  ]

  const ExtraAction = (props: any) => {
    const {searchKeyword} = useContext(DataGridContext)
    useEffect(() => {
      setSearchKeyword(searchKeyword)
    }, [searchKeyword])
    return (
      <Fragment>
      </Fragment>
    )
  }

  const toolbar = {
    title: t("common.Timecard"),
    searchTips: t("common.Search by Person ID, Person Name"),
    search: true,
    breadcrumbs: [
      {breadcrumbName: t("common.View Attendance Punch"), path: WebURL.ATT_TIMECARD_TIMECARD}
    ],
    extraComponent:
      <>
        <Grid sx={{paddingRight: "1vw"}}>
          <FormControl fullWidth sx={{
            marginLeft: "10px",
            width: "180px",
            display: 'flex',
            justifyContent: 'center'
          }}>
            <InputLabel
              id="verify-type-select-helper-label"
              sx={{
                textAlign: "center",
                width: '100%',
                left: 0,
                top: '50%',
                transform: 'translate(0, -50%) scale(0.85)',
                '&.MuiInputLabel-shrink': {
                  textAlign: "left",
                  left: 10,
                  top: 0,
                  transform: 'translate(0, -50%) scale(0.85)',
                },
                '&.Mui-focused': {
                  textAlign: "left",
                  left: 10,
                  top: 0,
                  transform: 'translate(0, -50%) scale(0.85)',
                }
              }}
            >
              {t('common.Verify Type')}
            </InputLabel>
            <Select
              labelId="department-select-label"
              id="department-select"
              value={verifyType}
              label={t('common.Verify Type')}
              size={'medium'}
              onChange={(e) => {
                setVerifyType(e.target.value)
                setFilterParams({...filterParams, verify_type: e.target.value})
              }}
              sx={{
                '& .MuiSelect-select': {
                  textAlign: 'center'
                }
              }}
            >
              <MenuItem key={''} value={''}>-----</MenuItem>
              {Object.entries(VERIFY_TYPE).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid sx={{paddingRight: "1vw"}}>
          <FormControlLabel
            labelPlacement="start"
            label={t("common.Show mended records")}
            control={<Switch color="primary" checked={mended} onChange={() => setMended(!mended)}/>}
          />
        </Grid>
      </>
    ,
    actions: [
      {
        key: 'about', iconFont: 'icon-ic_about', helpTxt: t("common.Usage Tips"),
        callback: async () => {
          setInfo(true)
        }
      }
    ],
    children: (
      <ExtraAction
        filterParams={filterParams}
        setExportFormat={setExportFormat}
      />
    ),
    extraComponentOnRight:
      <Grid sx={{display: "flex"}}>
        <Grid sx={{marginTop: "2px"}}>
          <ExportAction
            setExportFormat={async (fileType: string) => {
              setExportFormat(fileType);
            }}
            loading={exportLoading}
          />
        </Grid>
        <Grid sx={{marginTop: "2px"}}><TimeSelectDrawer dateFormat={dateFormat}/></Grid>
      </Grid>
  }

  return (
    <>
      <Box
        sx={{
          '& .MuiDataGrid-row-height-no-limit': {
            maxHeight: '180px!important'
          },
        }}
      >
        <CustomBreadcrumbs record={[{label: t("common.View Attendance Punch")}]}/>
        <CustomDataGrid
          columns={columns}
          ref={dataGridRef}
          uri={ATTEndpoints.ATT_TRANSACTION_URI}
          requestParams={filterParams}
          filterModel={filterDate}
          onFilterModelChange={(model) => {
            if (typeof (model.items[0].value[0]) == 'object') {
              model.items[0].value[0] = model.items[0].value[0].toISOString().slice(0, 10)
            }
            if (typeof (model.items[0].value[1]) == 'object') {
              model.items[0].value[1] = model.items[0].value[1].toISOString().slice(0, 10)
            }
            setFilterDate(model)
            setFilterParams({
              date_range: [
                model.items[0].value[0],
                model.items[0].value[1]
              ],
              verify_type: verifyType,
            })
            dataGridRef.current?.refresh(true);
          }}
          toolbar={toolbar}
          localeText={{
            filterPanelColumns: t("common.Columns"),
            filterPanelOperators: t("common.Operators"),
          }}
        />
      </Box>
      <InfoDialog/>
    </>
  )
}
export default TransactionGrid
